import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Clock, ChefHat, Truck, Package, MapPin, Phone, Navigation, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import API from "@/utils/api";

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom icons
const deliveryIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `<div style="background: #FFC107; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid #000; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2">
      <rect x="1" y="3" width="15" height="13"></rect>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
      <circle cx="5.5" cy="18.5" r="2.5"></circle>
      <circle cx="18.5" cy="18.5" r="2.5"></circle>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

const homeIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `<div style="background: #4CAF50; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid #fff; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" stroke="#fff" stroke-width="1">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const restaurantIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `<div style="background: #FF5722; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid #fff; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" stroke="#fff" stroke-width="1">
      <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
      <line x1="6" y1="1" x2="6" y2="4"></line>
      <line x1="10" y1="1" x2="10" y2="4"></line>
      <line x1="14" y1="1" x2="14" y2="4"></line>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});



const STATUS_STEPS = [
  { key: "pending", label: "Pendiente", icon: Clock },
  { key: "confirmed", label: "Confirmado", icon: Check },
  { key: "preparing", label: "Preparando", icon: ChefHat },
  { key: "ready", label: "Listo", icon: Package },
  { key: "on_the_way", label: "En camino", icon: Truck },
  { key: "delivered", label: "Entregado", icon: Check },
];

const STATUS_LABELS = {
  pending: "Pendiente de confirmación",
  confirmed: "Pedido confirmado",
  preparing: "Preparando tu pedido",
  ready: "Listo para recoger",
  on_the_way: "El repartidor está en camino",
  delivered: "¡Pedido entregado!",
  cancelled: "Pedido cancelado",
};

// Component to update map view when driver location changes
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

export default function OrderPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentChecked, setPaymentChecked] = useState(false);
  const mapRef = useRef(null);

  // Check payment status if returning from Stripe
  useEffect(() => {
    const checkPayment = async () => {
      const sessionId = searchParams.get("session_id");
      if (sessionId && !paymentChecked) {
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
          if (response.data.payment_status === "paid") {
            toast.success("¡Pago completado!");
            localStorage.removeItem("ztac_tacos_cart");
          }
        } catch (error) {
          console.error("Error checking payment:", error);
        }
        setPaymentChecked(true);
      }
    };
    checkPayment();
  }, [searchParams, paymentChecked]);

  // Fetch order details (try authenticated first, then public tracking)
  const fetchOrder = useCallback(async () => {
    try {
      // Try authenticated endpoint first
      const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setOrder(response.data);
    } catch (error) {
      // If auth fails, try public tracking endpoint for basic info
      if (error.response?.status === 401 || error.response?.status === 403) {
        try {
          const trackingResponse = await axios.get(`${API}/orders/${orderId}/tracking`);
          const trackingData = trackingResponse.data;
          // Create order object from tracking data
          setOrder({
            order_id: trackingData.order_id,
            status: trackingData.status,
            order_type: trackingData.order_type,
            address: trackingData.delivery_address,
            items: [], // Will be empty for public view
            total: 0,
            payment_method: "unknown",
            payment_status: "unknown"
          });
        } catch (trackingError) {
          console.error("Error fetching tracking:", trackingError);
          toast.error("Pedido no encontrado");
        }
      } else {
        console.error("Error fetching order:", error);
        toast.error("Error al cargar el pedido");
      }
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  // Fetch tracking info (including driver location)
  const fetchTracking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/tracking`);
      setTracking(response.data);
    } catch (error) {
      console.error("Error fetching tracking:", error);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    fetchTracking();

    // Poll for updates every 5 seconds when order is active
    const interval = setInterval(() => {
      fetchOrder();
      fetchTracking();
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchOrder, fetchTracking]);

  // Get current step index
  const getCurrentStepIndex = () => {
    if (!order) return 0;
    if (order.status === "cancelled") return -1;

    // For LOCAL and PICKUP orders, skip "on_the_way" step
    const isLocalOrPickup = order.order_type === "local" || order.order_type === "pickup";
    const steps = isLocalOrPickup
      ? STATUS_STEPS.filter(s => s.key !== "on_the_way")
      : STATUS_STEPS;

    return steps.findIndex(s => s.key === order.status);
  };

  // Get relevant steps for order type
  const getSteps = () => {
    if (!order) return STATUS_STEPS;
    const isLocalOrPickup = order.order_type === "local" || order.order_type === "pickup";
    return isLocalOrPickup
      ? STATUS_STEPS.filter(s => s.key !== "on_the_way")
      : STATUS_STEPS;
  };

  // Calculate map center and bounds
  const getMapConfig = () => {
    const restaurantPos = tracking?.restaurant_location || { lat: 36.8381, lng: -2.4597 };
    const driverPos = tracking?.driver_location;
    // Use real address coordinates if available
    const deliveryPos = order?.address?.lat && order?.address?.lng ? {
      lat: order.address.lat,
      lng: order.address.lng
    } : null;

    // If driver is on the way, center on driver
    if (driverPos && order?.status === "on_the_way") {
      return {
        center: [driverPos.lat, driverPos.lng],
        zoom: 15
      };
    }

    // Otherwise center on restaurant
    return {
      center: [restaurantPos.lat, restaurantPos.lng],
      zoom: 14
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-500 border-t-transparent" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Pedido no encontrado</p>
          <Button onClick={() => navigate("/")} className="bg-yellow-500 text-black">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const currentStepIndex = getCurrentStepIndex();
  const steps = getSteps();
  const mapConfig = getMapConfig();
  const isDelivery = order.order_type === "delivery";
  const isPickup = order.order_type === "pickup";
  const isLocal = order.order_type === "local";
  const isOnTheWay = order.status === "on_the_way";
  const hasDriverLocation = tracking?.driver_location?.lat && tracking?.driver_location?.lng;

  // Get order type label
  const getOrderTypeLabel = () => {
    if (isLocal) return "🍽️ Para comer aquí";
    if (isPickup) return "🛍️ Para recoger en tienda";
    return "🛵 Entrega a domicilio";
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-white">Pedido #{orderId.slice(-6)}</h1>
            <p className="text-sm text-gray-400">
              {getOrderTypeLabel()}
            </p>
          </div>
          <Badge className={order.status === "cancelled" ? "bg-red-500" : "bg-yellow-500 text-black"}>
            {STATUS_LABELS[order.status]}
          </Badge>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Live Map for Delivery Orders */}
        {isDelivery && (
          <Card className="bg-gray-900 border-gray-800 overflow-hidden">
            <CardContent className="p-0">
              <div className="relative">
                {/* Map */}
                <div className="h-64">
                  <MapContainer
                    center={mapConfig.center}
                    zoom={mapConfig.zoom}
                    style={{ height: "100%", width: "100%" }}
                    zoomControl={false}
                    ref={mapRef}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {/* Restaurant marker */}
                    <Marker
                      position={[tracking?.restaurant_location?.lat || 36.8381, tracking?.restaurant_location?.lng || -2.4597]}
                      icon={restaurantIcon}
                    >
                      <Popup>🍽️ ZTAC TACOS</Popup>
                    </Marker>

                    {/* Driver marker (only when on the way) */}
                    {isOnTheWay && hasDriverLocation && (
                      <Marker
                        position={[tracking.driver_location.lat, tracking.driver_location.lng]}
                        icon={deliveryIcon}
                      >
                        <Popup>
                          🛵 {tracking.driver_location.driver_name || "Repartidor"}
                          <br />
                          <small>Actualizado: {new Date(tracking.driver_location.updated_at).toLocaleTimeString("es-ES")}</small>
                        </Popup>
                      </Marker>
                    )}

                    {/* Delivery address marker */}
                    {order.address && order.address.lat && order.address.lng && (
                      <Marker
                        position={[order.address.lat, order.address.lng]}
                        icon={homeIcon}
                      >
                        <Popup>📍 {order.address.street}</Popup>
                      </Marker>
                    )}

                    {/* Route line when driver is on the way */}
                    {isOnTheWay && hasDriverLocation && order.address && order.address.lat && order.address.lng && (
                      <Polyline
                        positions={[
                          [tracking.driver_location.lat, tracking.driver_location.lng],
                          [order.address.lat, order.address.lng]
                        ]}
                        color="#FFC107"
                        weight={4}
                        dashArray="10, 10"
                      />
                    )}

                    <MapUpdater center={isOnTheWay && hasDriverLocation ? [tracking.driver_location.lat, tracking.driver_location.lng] : null} />
                  </MapContainer>
                </div>

                {/* Status overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
                  {isOnTheWay && hasDriverLocation ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center animate-pulse">
                        <Truck className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {tracking.driver_location.driver_name || "Tu repartidor"} está en camino
                        </p>
                        <p className="text-gray-400 text-sm">
                          Actualizado {new Date(tracking.driver_location.updated_at).toLocaleTimeString("es-ES")}
                        </p>
                      </div>
                    </div>
                  ) : isOnTheWay ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-yellow-500/50 rounded-full flex items-center justify-center">
                        <Truck className="w-6 h-6 text-yellow-500" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Pedido en camino</p>
                        <p className="text-gray-400 text-sm">Esperando ubicación del repartidor...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-gray-400">
                          El mapa se activará cuando el pedido esté en camino
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-white mb-6">Estado del pedido</h2>

            <div className="space-y-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isCompleted = currentStepIndex >= index;
                const isCurrent = currentStepIndex === index;

                return (
                  <div key={step.key} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isCompleted
                      ? isCurrent
                        ? "bg-yellow-500 animate-pulse"
                        : "bg-green-500"
                      : "bg-gray-700"
                      }`}>
                      <Icon className={`w-5 h-5 ${isCompleted ? "text-black" : "text-gray-400"}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isCompleted ? "text-white" : "text-gray-500"}`}>
                        {step.label}
                      </p>
                      {isCurrent && (
                        <p className="text-sm text-yellow-500">{STATUS_LABELS[order.status]}</p>
                      )}
                    </div>
                    {isCompleted && !isCurrent && (
                      <Check className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-white mb-4">Detalles del pedido</h2>

            {/* Items */}
            <div className="space-y-3 mb-6">
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between items-start">
                  <div>
                    <p className="text-white">{item.quantity}x {item.name}</p>
                    {item.extras?.length > 0 && (
                      <p className="text-sm text-gray-400">
                        +{item.extras.map(e => e.name).join(", ")}
                      </p>
                    )}
                  </div>
                  <p className="text-white">{(item.price * item.quantity).toFixed(2)} €</p>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-gray-800 pt-4 space-y-2">
              {order.delivery_fee > 0 && (
                <div className="flex justify-between text-gray-400">
                  <span>Gastos de envío</span>
                  <span>{order.delivery_fee.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span className="text-white">Total</span>
                <span className="text-yellow-500">{order.total.toFixed(2)} €</span>
              </div>
            </div>

            {/* Delivery Address */}
            {order.address && (
              <div className="mt-6 pt-4 border-t border-gray-800">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Dirección de entrega</p>
                    <p className="text-white">{order.address.street}</p>
                    <p className="text-gray-400">{order.address.postal_code} {order.address.city}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Table number for local orders */}
            {order.order_type === "local" && order.table_number && (
              <div className="mt-6 pt-4 border-t border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                    <span className="text-green-500 font-bold">{order.table_number}</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Número de mesa</p>
                    <p className="text-white">Mesa {order.table_number}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Pickup info */}
            {order.order_type === "pickup" && (
              <div className="mt-6 pt-4 border-t border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🛍️</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Recoger en tienda</p>
                    <p className="text-white">Te avisaremos cuando esté listo</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Status */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Método de pago</p>
                  <p className="text-white">
                    {order.payment_method === "card" ? "💳 Tarjeta" : "💵 Efectivo"}
                  </p>
                </div>
                <Badge className={order.payment_status === "paid" ? "bg-green-500" : "bg-orange-500"}>
                  {order.payment_status === "paid" ? "Pagado" : "Pendiente"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">¿Necesitas ayuda?</p>
                <p className="text-white">Contacta con el restaurante</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                onClick={() => window.open("tel:+34912345678")}
              >
                <Phone className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>
        {/* Order More Button */}
        <Button
          className="w-full h-14 bg-yellow-500 text-black font-bold text-lg rounded-xl hover:bg-yellow-400"
          onClick={() => {
            // Force preserve table state
            const currentTable = order?.table_number || localStorage.getItem("ztac_qr_table");
            if (currentTable) {
              localStorage.setItem("ztac_qr_table", currentTable);
              localStorage.setItem("ztac_table_confirmed", "true");
              navigate(`/menu?mesa=${currentTable}`);
            } else {
              navigate("/menu");
            }
          }}
        >
          🍽️ Pedir algo más
        </Button>
      </main>
    </div>
  );
}
